import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Grid,
  GridColumn,
  GridCellProps,
  GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useDealItems, useDeleteDealItem } from "src/dm/hooks/useDealItems";
import { useExportItemsExcel } from "src/dm/hooks/useExportItemsExcel";
import {
  fetchBulkOverrides,
  fetchBulkPromoGroups,
  fetchBulkStores,
  fetchBulkZones,
  useBulkApplyToAll,
  useBulkDelete,
  useBulkRemoveDealPricing,
  useBulkRemoveOverrides,
  useBulkRemovePromoGroup,
  useBulkRemoveStores,
  useBulkRemoveZones,
} from "src/dm/hooks/useDealItemBulk";
import { DealItem } from "src/dm/interfaces/DealItem";
import * as s from "./DealItems.css";

type BulkAction =
  | "delete"
  | "applyToAll"
  | "removeZones"
  | "removeOverrides"
  | "removeStores"
  | "removePromoGroup"
  | "removeDealPricing";

interface BulkState {
  action: BulkAction;
  // For the "pick one or more" modals
  options: Array<{ value: string; label: string }>;
  // For removeDealPricing modal — three checkboxes instead of a list
  pricingFlags?: { includeScan: boolean; includePromoPrice: boolean; includeAllowance: boolean };
  // For applyToAll — list of remaining items to copy onto
  sourceDealItemId?: number;
  loading: boolean;
  error: string | null;
}

/**
 * Action options shown in the bulk-action dropdown. Mirrors the legacy
 * `ddlSelectedItems` DropDownList on ViewDealItems.aspx — the user picks
 * one and clicks "Go" to fire it. The dropdown does NOT fire on change;
 * the "Go" button is the trigger so users have time to confirm their
 * action and the selected-items set.
 */
const BULK_ACTION_OPTIONS: Array<{ value: BulkAction; label: string }> = [
  { value: "applyToAll", label: "Apply to All" },
  { value: "removeZones", label: "Remove Zones" },
  { value: "removeOverrides", label: "Remove Overrides" },
  { value: "removeStores", label: "Remove Stores" },
  { value: "removePromoGroup", label: "Remove Promo Group" },
  { value: "removeDealPricing", label: "Remove Deal Pricing" },
  { value: "delete", label: "Delete" },
];

interface Props {
  dealId: number;
}

const DealItems = ({ dealId }: Props) => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useDealItems(dealId);
  const remove = useDeleteDealItem(dealId);

  // Row selection for bulk actions
  const [selectedDealItemIds, setSelectedDealItemIds] = useState<Set<number>>(new Set());
  // Two-step bulk-action UX: the dropdown stores the user's pick, and a
  // separate "Go" button fires the action. The dropdown does NOT trigger
  // on change — matches legacy ddlSelectedItems + btnGo on
  // ViewDealItems.aspx.
  const [bulkActionChoice, setBulkActionChoice] = useState<BulkAction | "">("");
  const [bulk, setBulk] = useState<BulkState | null>(null);
  const [bulkSelection, setBulkSelection] = useState<Set<string>>(new Set());
  const [appliedTargets, setAppliedTargets] = useState<Set<number>>(new Set());

  const bulkDelete = useBulkDelete(dealId);
  const bulkRemoveZones = useBulkRemoveZones(dealId);
  const bulkRemoveOverrides = useBulkRemoveOverrides(dealId);
  const bulkRemoveStores = useBulkRemoveStores(dealId);
  const bulkRemovePromoGroup = useBulkRemovePromoGroup(dealId);
  const bulkRemoveDealPricing = useBulkRemoveDealPricing(dealId);
  const bulkApplyToAll = useBulkApplyToAll(dealId);
  const exportExcel = useExportItemsExcel(dealId);

  const handleExportExcel = async () => {
    try {
      await exportExcel.mutateAsync();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Export failed");
    }
  };

  // Drop selections that point to items no longer on the deal (e.g. after delete)
  useEffect(() => {
    const valid = new Set((data ?? []).map((d) => d.dealItemId));
    setSelectedDealItemIds((prev) => {
      const next = new Set<number>();
      prev.forEach((id) => valid.has(id) && next.add(id));
      return next;
    });
  }, [data]);

  const toggleRow = (id: number) =>
    setSelectedDealItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelectedDealItemIds((prev) => {
      if (prev.size === (data?.length ?? 0)) return new Set();
      return new Set((data ?? []).map((d) => d.dealItemId));
    });

  const selectedArr = useMemo(() => Array.from(selectedDealItemIds), [selectedDealItemIds]);

  const openBulk = async (action: BulkAction) => {
    if (selectedDealItemIds.size === 0) {
      alert("Select at least one item first.");
      return;
    }
    // "Apply to All" no longer opens an in-place dialog — it now navigates
    // to the dedicated `/items/bulk-edit` page that replaces the legacy
    // `Item/BulkEditItems.aspx`. The other bulk actions (delete-style
    // operations) keep their dialogs because they're scoped to a single
    // pick-list each. See src/pages/BulkEditItems/BulkEditItems.tsx and
    // src/hooks/useBulkEditItems.ts.
    if (action === "applyToAll") {
      const ids = Array.from(selectedDealItemIds).join(",");
      navigate(`/dm/deals/${dealId}/items/bulk-edit?ids=${ids}`);
      return;
    }

    setBulkSelection(new Set());
    setAppliedTargets(new Set());
    setBulk({ action, options: [], loading: true, error: null });

    try {
      if (action === "delete") {
        // No follow-up modal — run immediately after confirmation
        setBulk({ action, options: [], loading: false, error: null });
      } else if (action === "removeZones") {
        const zones = await fetchBulkZones(dealId, selectedArr);
        setBulk({
          action,
          options: zones.map((z) => ({ value: String(z), label: `Zone ${z}` })),
          loading: false,
          error: null,
        });
      } else if (action === "removeOverrides") {
        const items = await fetchBulkOverrides(dealId, selectedArr);
        setBulk({
          action,
          options: items.map((o) => ({
            value: `${o.zoneNumber}:${o.overrideNumber}`,
            label: `Zone ${o.zoneNumber} / Override ${o.overrideNumber}`,
          })),
          loading: false,
          error: null,
        });
      } else if (action === "removeStores") {
        const stores = await fetchBulkStores(dealId, selectedArr);
        setBulk({
          action,
          options: stores.map((st) => ({ value: String(st), label: `Store ${st}` })),
          loading: false,
          error: null,
        });
      } else if (action === "removePromoGroup") {
        const groups = await fetchBulkPromoGroups(dealId, selectedArr);
        setBulk({
          action,
          options: groups.map((g) => ({ value: String(g), label: `Promo Group ${g}` })),
          loading: false,
          error: null,
        });
      } else if (action === "removeDealPricing") {
        setBulk({
          action,
          options: [],
          pricingFlags: { includeScan: false, includePromoPrice: false, includeAllowance: false },
          loading: false,
          error: null,
        });
      } else if (action === "applyToAll") {
        if (selectedDealItemIds.size < 2) {
          setBulk(null);
          alert("Pick the source item plus at least one target item (2+ rows selected).");
          return;
        }
        setBulk({
          action,
          options: selectedArr.map((id) => ({ value: String(id), label: `Deal Item #${id}` })),
          sourceDealItemId: selectedArr[0],
          loading: false,
          error: null,
        });
      }
    } catch (err) {
      setBulk({ action, options: [], loading: false, error: err instanceof Error ? err.message : "Lookup failed" });
    }
  };

  const closeBulk = () => {
    setBulk(null);
    setBulkSelection(new Set());
    setAppliedTargets(new Set());
  };

  const confirmBulk = async () => {
    if (!bulk) return;
    try {
      if (bulk.action === "delete") {
        if (!confirm(`Delete ${selectedDealItemIds.size} item(s) from this deal? This cannot be undone.`)) return;
        await bulkDelete.mutateAsync({ dealItemIds: selectedArr });
      } else if (bulk.action === "removeZones") {
        const zones = Array.from(bulkSelection).map(Number);
        await bulkRemoveZones.mutateAsync({ dealItemIds: selectedArr, zoneNumbers: zones });
      } else if (bulk.action === "removeOverrides") {
        const pairs = Array.from(bulkSelection).map((v) => {
          const [z, o] = v.split(":").map(Number);
          return { zoneNumber: z, overrideNumber: o };
        });
        await bulkRemoveOverrides.mutateAsync({ dealItemIds: selectedArr, overrides: pairs });
      } else if (bulk.action === "removeStores") {
        const stores = Array.from(bulkSelection).map(Number);
        await bulkRemoveStores.mutateAsync({ dealItemIds: selectedArr, storeNumbers: stores });
      } else if (bulk.action === "removePromoGroup") {
        const groups = Array.from(bulkSelection).map(Number);
        await bulkRemovePromoGroup.mutateAsync({ dealItemIds: selectedArr, promoGroupIds: groups });
      } else if (bulk.action === "removeDealPricing") {
        const flags = bulk.pricingFlags!;
        if (!flags.includeScan && !flags.includePromoPrice && !flags.includeAllowance) {
          setBulk({ ...bulk, error: "Pick at least one row type." });
          return;
        }
        await bulkRemoveDealPricing.mutateAsync({ dealItemIds: selectedArr, ...flags });
      } else if (bulk.action === "applyToAll") {
        const source = bulk.sourceDealItemId!;
        const targets = Array.from(appliedTargets).filter((id) => id !== source);
        if (targets.length === 0) {
          setBulk({ ...bulk, error: "Pick at least one target item (cannot equal source)." });
          return;
        }
        await bulkApplyToAll.mutateAsync({ sourceDealItemId: source, targetDealItemIds: targets });
      }
      closeBulk();
    } catch (err) {
      setBulk({ ...bulk, error: err instanceof Error ? err.message : "Operation failed" });
    }
  };

  const handleRowClick = (e: GridRowClickEvent) => {
    const row = e.dataItem as DealItem;
    // Land on Promo Prices by default — this is by far the most common
    // edit workflow on the item-detail page. The destination page reads
    // `?tab=` and falls back to Summary if the param is missing, so direct
    // deep-links without the query string still default to Summary.
    // audit/26-gap-closure-verification.md §3 item 8.
    if (row?.dealItemId)
      navigate(`/dm/deals/${dealId}/items/${row.dealItemId}?tab=promo-prices`);
  };

  const ActionsCell = (props: GridCellProps) => {
    const row = props.dataItem as DealItem;
    return (
      <td>
        <Button
          type="button"
          themeColor="error"
          size="small"
          fillMode="flat"
          onClick={() => {
            if (confirm(`Remove item ${row.retailItemNumber} from this deal?`))
              remove.mutate(row.dealItemId);
          }}
          disabled={remove.isPending}
        >
          Remove
        </Button>
      </td>
    );
  };

  return (
    <section className={s.card}>
      <div className={s.header}>
        <h2 className={s.title}>Items ({data?.length ?? 0})</h2>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <label style={{ fontSize: "0.8rem", color: "#64748b", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
            <input
              type="checkbox"
              aria-label="Select all items"
              checked={(data?.length ?? 0) > 0 && selectedDealItemIds.size === (data?.length ?? 0)}
              onChange={toggleAll}
            />
            {selectedDealItemIds.size > 0
              ? `${selectedDealItemIds.size} selected`
              : "select all"}
          </label>
          {/*
           * Legacy parity (`ViewDealItems.aspx` ddlSelectedItems + btnGo
           * + ItemsWereSelected() guard): the dropdown is just a picker
           * — the action only fires when the user clicks "Go", and the
           * button is gated on both (a) at least one item checked and
           * (b) an action selected. This avoids the prior auto-fire UX
           * that triggered destructive flows on every onChange.
           */}
          <select
            aria-label="Bulk action"
            style={{ fontSize: "0.85rem", padding: "0.3rem 0.5rem" }}
            value={bulkActionChoice}
            onChange={(e) =>
              setBulkActionChoice((e.target.value as BulkAction | "") ?? "")
            }
          >
            <option value="">— Bulk action —</option>
            {BULK_ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <Button
            type="button"
            themeColor="primary"
            fillMode="solid"
            size="small"
            disabled={
              selectedDealItemIds.size === 0 || bulkActionChoice === ""
            }
            onClick={() => {
              if (!bulkActionChoice) return;
              openBulk(bulkActionChoice);
              // After firing, leave the picker as-is so the user can
              // see what they ran; the openBulk handlers manage their
              // own follow-up state.
            }}
          >
            Go
          </Button>
          <Button
            type="button"
            fillMode="outline"
            onClick={handleExportExcel}
            disabled={exportExcel.isPending || (data?.length ?? 0) === 0}
            title="Download every item on this deal as an .xlsx (legacy ShowItemExcel.aspx)"
          >
            {exportExcel.isPending ? "Exporting…" : "Export to Excel"}
          </Button>
          <Button
            type="button"
            themeColor="primary"
            // Add Item now navigates to the full-page route (replaces the
            // old in-place Kendo Dialog). The page itself reuses the
            // family-prompt + incomplete-families modals and the
            // useRetailItemSearch hook. See src/pages/AddItem/AddItem.tsx.
            onClick={() => navigate(`/dm/deals/${dealId}/items/add`)}
          >
            Add item
          </Button>
        </div>
      </div>

      {isLoading && <p className={s.empty}>Loading items…</p>}
      {isError && (
        <p style={{ color: "#b91c1c" }}>
          {error instanceof Error ? error.message : "Failed to load items"}
        </p>
      )}
      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <p className={s.empty}>No items on this deal yet.</p>
      )}

      {(data?.length ?? 0) > 0 && (
        <Grid data={data} onRowClick={handleRowClick} style={{ cursor: "pointer" }}>
          <GridColumn
            title="✓"
            width="40px"
            cells={{
              data: (props: GridCellProps) => {
                const id = (props.dataItem as DealItem).dealItemId;
                return (
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedDealItemIds.has(id)}
                      onChange={() => toggleRow(id)}
                      aria-label={`Select item ${id}`}
                    />
                  </td>
                );
              },
            }}
          />
          <GridColumn field="dealItemId" title="#" width="60px" />
          <GridColumn field="retailItemNumber" title="Retail Item #" width="140px" />
          <GridColumn field="itemDescription" title="Description" width="320px" />
          <GridColumn field="priceGroupCode" title="Price Grp" width="100px" />
          <GridColumn field="supplierNumber" title="Supplier" width="120px" />
          <GridColumn field="familyCode" title="Family" width="100px" />
          <GridColumn field="addedByFamily" title="Full Family" width="100px" />
          <GridColumn field="regularPrice" title="Reg Price" width="100px" format="{0:c2}" />
          <GridColumn field="projectedUnits" title="Proj Units" width="120px" />
          <GridColumn field="projectedUnitsUom" title="UOM" width="80px" />
          <GridColumn field="warehouseFacilityNumber" title="WH" width="80px" />
          <GridColumn field="rewardItemTypeCode" title="Reward" width="100px" />
          <GridColumn title="" width="120px" cells={{ data: ActionsCell }} />
        </Grid>
      )}

      {bulk && (
        <Dialog
          title={
            bulk.action === "delete" ? "Delete items"
            : bulk.action === "applyToAll" ? "Apply to All"
            : bulk.action === "removeZones" ? "Remove Zones"
            : bulk.action === "removeOverrides" ? "Remove Overrides"
            : bulk.action === "removeStores" ? "Remove Stores"
            : bulk.action === "removePromoGroup" ? "Remove Promo Group"
            : "Remove Deal Pricing"
          }
          width={520}
          onClose={closeBulk}
        >
          {bulk.loading && <p>Loading options…</p>}
          {bulk.error && (
            <p style={{ color: "#b91c1c", margin: 0 }}>{bulk.error}</p>
          )}

          {bulk.action === "delete" && !bulk.loading && (
            <p style={{ margin: 0 }}>
              {selectedDealItemIds.size} item(s) will be removed from this deal, including any
              promo-price, scan, and allowance rows.
            </p>
          )}

          {bulk.action === "applyToAll" && !bulk.loading && (
            <div style={{ fontSize: "0.9rem" }}>
              <p style={{ marginTop: 0 }}>
                Pick one source item, then check the target items. The source's
                promo prices, scans, and allowances will replace those of every
                target.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label>
                  <span className={s.fieldLabel}>Source item</span>
                  <select
                    value={bulk.sourceDealItemId ?? ""}
                    onChange={(e) =>
                      setBulk({ ...bulk, sourceDealItemId: Number(e.target.value), error: null })
                    }
                    style={{ width: "100%", padding: "0.3rem", border: "1px solid #cbd5e1", borderRadius: 4 }}
                  >
                    {bulk.options.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <div>
                  <div className={s.fieldLabel}>Targets</div>
                  <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #e2e8f0", padding: "0.5rem", borderRadius: 4 }}>
                    {bulk.options
                      .filter((o) => Number(o.value) !== bulk.sourceDealItemId)
                      .map((o) => {
                        const id = Number(o.value);
                        return (
                          <label key={o.value} style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.2rem 0" }}>
                            <input
                              type="checkbox"
                              checked={appliedTargets.has(id)}
                              onChange={(e) => {
                                const next = new Set(appliedTargets);
                                if (e.target.checked) next.add(id);
                                else next.delete(id);
                                setAppliedTargets(next);
                              }}
                            />
                            {o.label}
                          </label>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {bulk.action === "removeDealPricing" && !bulk.loading && bulk.pricingFlags && (
            <div style={{ fontSize: "0.9rem" }}>
              <p style={{ marginTop: 0 }}>Pick which child-row types to delete for {selectedDealItemIds.size} item(s):</p>
              <label style={{ display: "block", padding: "0.2rem 0" }}>
                <input
                  type="checkbox"
                  checked={bulk.pricingFlags.includePromoPrice}
                  onChange={(e) =>
                    setBulk({
                      ...bulk,
                      pricingFlags: { ...bulk.pricingFlags!, includePromoPrice: e.target.checked },
                      error: null,
                    })
                  }
                />{" "}
                Promo prices (DEAL_ITEM_PROMO_PRICE)
              </label>
              <label style={{ display: "block", padding: "0.2rem 0" }}>
                <input
                  type="checkbox"
                  checked={bulk.pricingFlags.includeScan}
                  onChange={(e) =>
                    setBulk({
                      ...bulk,
                      pricingFlags: { ...bulk.pricingFlags!, includeScan: e.target.checked },
                      error: null,
                    })
                  }
                />{" "}
                Scans (DEAL_ITEM_SCAN)
              </label>
              <label style={{ display: "block", padding: "0.2rem 0" }}>
                <input
                  type="checkbox"
                  checked={bulk.pricingFlags.includeAllowance}
                  onChange={(e) =>
                    setBulk({
                      ...bulk,
                      pricingFlags: { ...bulk.pricingFlags!, includeAllowance: e.target.checked },
                      error: null,
                    })
                  }
                />{" "}
                Allowances (DEAL_ITEM_ALLOWANCE)
              </label>
            </div>
          )}

          {(bulk.action === "removeZones" ||
            bulk.action === "removeOverrides" ||
            bulk.action === "removeStores" ||
            bulk.action === "removePromoGroup") &&
            !bulk.loading && (
              <div style={{ fontSize: "0.9rem" }}>
                {bulk.options.length === 0 ? (
                  <p style={{ color: "#94a3b8", margin: 0 }}>
                    No matching rows exist on the selected items.
                  </p>
                ) : (
                  <>
                    <p style={{ marginTop: 0 }}>
                      Pick one or more entries to remove from {selectedDealItemIds.size} item(s):
                    </p>
                    <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #e2e8f0", padding: "0.5rem", borderRadius: 4 }}>
                      {bulk.options.map((o) => (
                        <label key={o.value} style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.2rem 0" }}>
                          <input
                            type="checkbox"
                            checked={bulkSelection.has(o.value)}
                            onChange={(e) => {
                              const next = new Set(bulkSelection);
                              if (e.target.checked) next.add(o.value);
                              else next.delete(o.value);
                              setBulkSelection(next);
                            }}
                          />
                          {o.label}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

          <DialogActionsBar>
            <Button onClick={closeBulk}>Cancel</Button>
            <Button
              themeColor="primary"
              disabled={bulk.loading}
              onClick={confirmBulk}
            >
              {bulk.action === "delete" ? "Delete" : "Apply"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </section>
  );
};

export default DealItems;
